import {
  PaymentTable,
  PaymentTableRow,
  PaymentType as StrategyPaymentType,
  ProgramSplitFormat,
  ResolutionType,
  StrategyConfigV2,
  StrategyFormat,
  StrategyResponse,
  StrategyResponseUiFields,
  TargetWeeklyCalculationStrategy,
  GuardRails,
  StrategyGenerationClientContext,
  InterestCalculationStrategy,
  StrategyRuleKeys,
  ExtraFee,
  CreditorPayFormat,
  InitialPaymentPercentageOf,
} from "../../dtos/strategy.dto";
import {
  ApplicationDto,
  ApplicationSchemaDto,
  T_CustomStrategy as CustomStrategy,
  ManagerApprovalStatus,
  PaymentEntryTable,
} from "../../dtos/application.dto";
import {
  floorToClosest99,
  isTruthful,
  parseNumber,
  randomBytes,
  round,
  safeDivide,
  slugify,
  asPercentage,
  omitAndMakeOptional,
} from "../functions";
import { Merge } from "types/_utils.types";
import {
  CreateLedgerDto,
  DistributionType,
  LEDGER_CATEGORY,
  LedgerDto,
  LOCK_STATUS,
  TRANSACTION_STATUS,
  TRANSACTION_TYPE,
} from "../../dtos/ledger.dto";
import z from "zod";

const getLawsuitCost = (context: StrategyGenerationClientContext) => {
  let initial = 0,
    monthly = 0;
  if (context.lawsuits > 0) {
    initial = context.with_lawsuit_pay.one_time * context.lawsuits;
    monthly = context.with_lawsuit_pay.monthly * context.lawsuits;
  } else {
    initial = context.no_lawsuit_pay.one_time;
    monthly = context.no_lawsuit_pay.monthly;
  }

  return { initial, monthly, weekly: monthly / 4 };
};

const calculateExtraFee = (
  context: StrategyGenerationClientContext,
  strategy?: Pick<StrategyConfigV2, "service_fee" | "legal_fee">,
) => {
  const service = { initial: 25, weekly: 25 };
  const baseLegal = getLawsuitCost(context);
  const legal = { initial: baseLegal.initial, weekly: baseLegal.weekly };

  const parameters: ExtraFee.ApplyCondition[] = [];
  if (context.lawsuits > 0) parameters.push(ExtraFee.ApplyCondition.HAS_LAWSUITS);
  else parameters.push(ExtraFee.ApplyCondition.NO_LAWSUITS);

  const findMatchingConfig = (configs: ExtraFee.Config[], params: ExtraFee.ApplyCondition[]) => {
    return configs.find((config) => {
      for (const condition of config.applyIf) {
        if (!params.includes(condition)) {
          return false;
        }
      }

      return true;
    });
  };

  const calculatePrice = (config: ExtraFee.Config) => {
    switch (config.mode) {
      case ExtraFee.CalculationMode.FIXED: {
        return config.value;
      }
      case ExtraFee.CalculationMode.PER_LAWSUIT: {
        return config.value * context.lawsuits;
      }
      // case ExtraFee.CalculationMode.PER_LOAN: {
      //   return config.value * context.loans;
      // }
    }
  };

  if (strategy?.service_fee) {
    const serviceBaseConfig = findMatchingConfig(strategy.service_fee, parameters);
    const initialServiceConfig =
      findMatchingConfig(strategy.service_fee, [...parameters, ExtraFee.ApplyCondition.IS_INITIAL]) ??
      serviceBaseConfig;
    const weeklyServiceConfig =
      findMatchingConfig(strategy.service_fee, [...parameters, ExtraFee.ApplyCondition.NOT_INITIAL]) ??
      serviceBaseConfig;

    if (initialServiceConfig) {
      service.initial = calculatePrice(initialServiceConfig);
    }

    if (weeklyServiceConfig) {
      service.weekly = calculatePrice(weeklyServiceConfig);
    }
  }

  if (strategy?.legal_fee) {
    const legalBaseConfig = findMatchingConfig(strategy.legal_fee, parameters);
    const initialLegalConfig =
      findMatchingConfig(strategy.legal_fee, [...parameters, ExtraFee.ApplyCondition.IS_INITIAL]) ?? legalBaseConfig;
    const weeklyLegalConfig =
      findMatchingConfig(strategy.legal_fee, [...parameters, ExtraFee.ApplyCondition.NOT_INITIAL]) ?? legalBaseConfig;

    if (initialLegalConfig) {
      legal.initial = calculatePrice(initialLegalConfig);
    }

    if (weeklyLegalConfig) {
      legal.weekly = calculatePrice(weeklyLegalConfig);
    }
  }

  return { service, legal };
};

type Mutation<T> = {
  seq: number;
  oldValue: T | null;
  newValue: T | null;
  /** e.g: program weeks increased */
  decision: string;
  /** e.g: MAX_PROGRAM_TENURE was very high */
  reasons: string[];
  /** e.g: `getTenure` | `getInterestWeeks` */
  actor: string;
  context?: unknown;
};

class TrackableValue<T = unknown> {
  private readonly mutations: Mutation<T>[] = [];
  constructor(private _value: T | null = null) {}

  get value() {
    return this._value ?? null;
  }

  update(newValue: T, meta: Omit<Mutation<T>, "seq" | "oldValue" | "newValue">) {
    const oldValue = this.value;
    this._value = newValue;
    this.mutations.push({
      ...meta,
      oldValue,
      newValue,
      seq: this.mutations.length + 1,
    });
  }
}

export class StrategyGenerator {
  private totalPayable = new TrackableValue<number>();
  private initialPayment = new TrackableValue<number>();
  private initialServiceFee = 0;
  private weeklyServiceFee = 0;
  private initialLegalFee = 0;
  private weeklyLegalFee = 0;

  constructor(
    private readonly context: StrategyGenerationClientContext,
    private readonly config: Merge<StrategyConfigV2, { index: number }>,
  ) {
    this.compute();
  }

  private compute() {
    const { service, legal } = calculateExtraFee(this.context, this.config);
    this.initialServiceFee = service.initial;
    this.weeklyServiceFee = service.weekly;
    this.initialLegalFee = legal.initial;
    this.weeklyLegalFee = legal.weekly;
  }

  private getRuleValue(key: StrategyRuleKeys) {
    return this.config.rules?.find((rule) => rule.key === key)?.value;
  }

  private getAdjustedValue(
    value: number,
    minRule: StrategyRuleKeys | undefined,
    maxRule: StrategyRuleKeys | undefined,
  ) {
    const min = minRule ? this.getRuleValue(minRule) : undefined;
    const max = maxRule ? this.getRuleValue(maxRule) : undefined;

    return Math.min(Math.max(min ?? value, value), max ?? value);
  }

  /**
   * If returned value is 0, then it's fine
   */
  private validateValue(value: number, minRule: StrategyRuleKeys | undefined, maxRule: StrategyRuleKeys | undefined) {
    const min = minRule ? this.getRuleValue(minRule) : undefined;
    const max = maxRule ? this.getRuleValue(maxRule) : undefined;

    if (min !== undefined && value < min) return -1;
    if (max !== undefined && value > max) return 1;
    return 0;
  }

  private getLawsuitCost() {
    return { initial: this.initialLegalFee, weekly: this.weeklyLegalFee };
  }

  private hasManagersApproval() {
    return (
      this.config.format === StrategyFormat.MANAGERS_APPROVAL &&
      [ManagerApprovalStatus.REQUESTED, ManagerApprovalStatus.APPROVED, ManagerApprovalStatus.SUGGESTED].includes(
        this.context.manager_approval_status,
      )
    );
  }

  /**
   * COMPONENTS:
   *
   * initial_fee + initial_extra_fee
   */
  private getInitialPaymentAmount() {
    let base_initial_payment =
      (this.config.initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST
        ? this.getTotalInterestAmount(true)
        : this.context.total_debt) * this.config.initial_fee;
    base_initial_payment += this.config.initial_bias_amount;
    base_initial_payment += this.config.creditor_split.initial_bias_amount;

    base_initial_payment += this.getInitialExtraAmount();
    return base_initial_payment;
  }

  /**
   * COMPONENTS:
   * `total_interest`
   *
   * FORMULA BREAKDOWN:
   * `if (strategy == 'percentage')
   *      total_interest = total_debt * (program_fee + success_fee)
   *  else
   *      total_interest = interest_amount
   * `
   */
  private getTotalInterestAmount(base = false) {
    let totalInterest = 0;
    const interestAmount = this.config.interest_amount ?? 0;

    switch (this.config.interest_calculation_strategy) {
      case InterestCalculationStrategy.FIXED: {
        totalInterest = interestAmount;
        break;
      }
      case InterestCalculationStrategy.FIXED_PER_LAWSUIT: {
        totalInterest = interestAmount * this.context.lawsuits;
        break;
      }
      case InterestCalculationStrategy.FIXED_PER_LOAN: {
        totalInterest = interestAmount * this.context.loans;
        break;
      }
    }

    if (this.config.interest_calculation_strategy === InterestCalculationStrategy.PERCENTAGE || !totalInterest) {
      const interest_multiplier = this.config.program_fee + this.config.success_fee;
      totalInterest = this.context.total_debt * interest_multiplier;
    }

    // If bias is not inclusive then it gets added to interest
    if (!base && this.config.is_bias_exclusive) {
      totalInterest += this.config.initial_bias_amount ?? 0;
    }

    return this.getAdjustedValue(totalInterest, StrategyRuleKeys.MIN_INTEREST, StrategyRuleKeys.MAX_INTEREST);
  }

  /**
   * COMPONENTS:
   * `total_creditor_payable`
   *
   * FORMULA BREAKDOWN:
   * `total_creditor_payable = total_debt * (percentage_of_payable)`
   *
   * NOTE: for strat-1 the percentage is 78%, otherwise it's 100%
   */
  private getTotalCreditorPaybackAmount() {
    return this.config.creditor_split.pay_format === CreditorPayFormat.DIRECT
      ? 0
      : this.context.total_debt * (this.config?.creditor_split?.total_payback ?? 1);
  }

  private getInitialExtraAmount() {
    return this.initialServiceFee + this.initialLegalFee;
  }

  /**
   * COMPONENTS:
   *
   * total_interest + total_creditor_payable + service_fee (1w) + initial_legal_fee
   */
  private getTotalPayableAmount() {
    const total_interest = this.getTotalInterestAmount();
    const creditor_payback = this.getTotalCreditorPaybackAmount();
    const extra_amount = this.getInitialExtraAmount();

    return creditor_payback + total_interest + extra_amount;
  }

  private getDebtBandWeeks() {
    const total_debt = this.context.total_debt;

    let total: number;
    let interest: number;

    if (total_debt < 25_001) {
      total = 4.33 * 6;
      interest = 15;
    } else if (total_debt < 50_001) {
      total = 4.33 * 8;
      interest = 20;
    } else if (total_debt < 125_001) {
      total = 4.33 * 12;
      interest = 30;
    } else if (total_debt < 250_001) {
      total = 4.33 * 18;
      interest = 40;
    } else if (total_debt < 500_001) {
      total = 4.33 * 24;
      interest = 50;
    } else {
      total = 4.33 * 30;
      interest = 60;
    }

    total = Math.ceil(total);
    interest = Math.ceil(interest);

    return { total, interest };
  }

  private getEstimatedTotalWeeks() {
    if (this.config.payment_type === StrategyPaymentType.ONE_TIME) return 0;
    let weekly_budget: number | undefined = undefined;
    if (this.config.format === StrategyFormat.MANAGERS_APPROVAL) {
      const stored = z.coerce.number().safeParse(this.config.meta?.weekly_budget).data;

      if (stored) {
        weekly_budget = stored;
      } else if (this.hasManagersApproval()) {
        weekly_budget = this.context.weekly_budget;
      }
    } else if (this.config.format === StrategyFormat.CUSTOM) {
      weekly_budget = this.context.weekly_budget;
    }

    const base_weeks = this.getDebtBandWeeks();
    const total_interest = this.getTotalInterestAmount();
    const total_payable = total_interest + this.getTotalCreditorPaybackAmount();
    let total_weeks = base_weeks.total;
    if (this.context.lawsuits > 0) {
      // Lawsuit guardrail: target = 1.75% of debt per week
      const weekly_target = this.context.total_debt * 0.0175;
      total_weeks = Math.ceil(total_payable / weekly_target);
    } else if (this.config.format === StrategyFormat.CUSTOM && this.context.total_weeks) {
      total_weeks = this.context.total_weeks;
    } else if (this.config.format === StrategyFormat.DEFAULT && this.context.current_weekly_pay) {
      // Target weekly calculation based on strategy configuration
      switch (this.config.target_weekly_calculation_strategy) {
        case TargetWeeklyCalculationStrategy.FIXED: {
          break;
        }
        case TargetWeeklyCalculationStrategy.LOW_WEEKLY: {
          // const factor = 0.5;
          // const adjusted_weekly =
          //     this.context.current_weekly_pay * factor;
          // total_weeks = Math.ceil(total_payable / adjusted_weekly);
          throw new Error("Low weekly is not supported yet");
        }
        case TargetWeeklyCalculationStrategy.PRIOR_BIAS: {
          const factor = 0.5;
          const adjusted_weekly = this.context.current_weekly_pay * factor;
          total_weeks = Math.ceil(total_payable / adjusted_weekly);
          break;
        }
        default:
          throw new Error("Invalid target weekly calculation strategy");
      }
    } else if (
      [StrategyFormat.CUSTOM, StrategyFormat.MANAGERS_APPROVAL].includes(this.config.format) &&
      weekly_budget
    ) {
      const weekly_extra = this.context.service_weekly + this.weeklyLegalFee;
      const effective_weekly_budget = weekly_budget - weekly_extra;
      total_weeks = Math.ceil(total_payable / effective_weekly_budget);
    }

    if (this.config.dynamic_weekly_pushback) {
      const pushback_factor = 1 + this.config.dynamic_weekly_pushback;
      total_weeks = Math.ceil(total_weeks * pushback_factor);
    }

    total_weeks = Math.ceil(
      this.getAdjustedValue(total_weeks, StrategyRuleKeys.MIN_TENURE, StrategyRuleKeys.MAX_TENURE),
    );

    /**
            Suppose x weeks are getting stretched to y months
            Strategy Invariant: You never pay twice for 1 one payment cycle (day/week/month)
            
            So once a week if payment_type is week, or once a month if payment_type is month.
            
            But in total we have 1 + x weeks (where 1 is initial)
            So in this case it will be 1 + y months => 4 * (1 + x) weeks => 4 + 4x weeks
            4 + 4x = 1 + y
            y = 4x + 3
            
            This piece of comment is not AI generated and I take pride in it
         */
    if (this.config.payment_type === StrategyPaymentType.MONTHLY) {
      total_weeks = total_weeks * 4 + 3;
    }

    return total_weeks;
  }

  private getInterestWeeks({ total_weeks }: { total_weeks: number }) {
    if (this.config.payment_type === StrategyPaymentType.ONE_TIME) return 0;

    const base_weeks = this.getDebtBandWeeks();
    const total_interest = this.getTotalInterestAmount();
    const total_payable = total_interest + this.getTotalCreditorPaybackAmount();

    const skew_factor = total_weeks / base_weeks.total;
    let interest_weeks = Math.ceil(skew_factor * base_weeks.interest);

    // If initial fee covers all interest, then no need to pay weekly
    if (
      this.config.interest_calculation_strategy === InterestCalculationStrategy.PERCENTAGE &&
      this.config.initial_fee === this.config.program_fee + this.config.success_fee
    ) {
      interest_weeks = 0;
      // Evenly distributed
    } else if (this.config.program_split.format === ProgramSplitFormat.UNIFIED) {
      interest_weeks = total_weeks;
    } else if (this.config.program_split.format === ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE) {
      const percentage = asPercentage(this.config.program_split.meta.percentage);

      if (percentage) {
        interest_weeks = total_weeks * percentage;
      }
    } else if (this.config.program_split.format === ProgramSplitFormat.PERCENTAGE_OF_REVENUE) {
      const percentage = asPercentage(this.config.program_split.meta.percentage);

      if (this.context.monthly_revenue && percentage) {
        const weekly_revenue = this.context.monthly_revenue / 4;
        const weekly_interest = weekly_revenue * percentage;
        interest_weeks = total_interest / weekly_interest;
      }
    } else if (this.config.program_split.format === ProgramSplitFormat.EARLY_STOPPING_WEEKS) {
      let weeks: number | null = null;
      let debt_cap = 0;

      const {
        steps = [],
        mode = "variable",
        base = "debt",
        fixed_weeks,
        percentage,
      } = this.config.program_split?.meta ?? {};

      if (mode === "variable" && steps && Array.isArray(steps)) {
        const baseAmount = base === "interest" ? this.getTotalInterestAmount() : this.context.total_debt;

        for (const step of steps) {
          const dc = parseNumber(step?.debt_cap);
          const w = parseNumber(step?.weeks);
          if (dc === null || w === null) continue;
          if (baseAmount <= dc && debt_cap < dc) {
            weeks = w;
            debt_cap = dc;
            break;
          }
        }
      } else if (mode === "fixed" && fixed_weeks !== undefined) {
        weeks = parseNumber(fixed_weeks);
      }

      weeks ??= parseNumber(percentage);

      if (weeks) {
        interest_weeks = weeks;
      } else {
        console.log("EARLY STOPPING WEEKS was not applied");
      }
    } else if (this.config.program_split.format === ProgramSplitFormat.PERCENTAGE_OF_WEEKLY) {
      const percentage = asPercentage(this.config.program_split.meta.percentage);

      if (percentage) {
        const weekly_total = safeDivide(total_payable, total_weeks);
        const weekly_interest = percentage * weekly_total;
        interest_weeks = safeDivide(total_interest, weekly_interest);
      }
    }

    return this.getAdjustedValue(
      Math.ceil(interest_weeks),
      StrategyRuleKeys.MIN_PROGRAM_TENURE,
      StrategyRuleKeys.MAX_PROGRAM_TENURE,
    );
  }

  private getTenure() {
    const total_weeks = this.getEstimatedTotalWeeks();
    const interest_weeks = this.getInterestWeeks({ total_weeks });

    return { total_weeks, interest_weeks };
  }

  private calculateDisplayTimeline() {
    const { total_weeks } = this.getTenure();
    let weeks: number | undefined = undefined;
    // (config.display_timeline ?? [])
    //     .sort((a, b) => a.debt_cap - b.debt_cap)
    //     .forEach((dt) => {
    //         if (dt.debt_cap >= this.context.total_debt) {
    //             weeks = dt.weeks;
    //         }
    //     });

    if (!weeks && this.config.fallback_display_timeline) {
      const total_payback = this.getTotalPayableAmount();
      const initial_extra_amount = this.getInitialExtraAmount();
      const weekly_extra = this.context.service_weekly + this.weeklyLegalFee;
      const weekly_total = total_weeks
        ? Math.ceil((total_payback - initial_extra_amount) / total_weeks) + weekly_extra
        : 0;

      weeks = Math.ceil(
        safeDivide(this.context.total_debt * this.config.fallback_display_timeline, weekly_total - weekly_extra),
      );
    }

    weeks ??= total_weeks;
    return Math.min(
      this.getAdjustedValue(weeks, StrategyRuleKeys.MIN_TENURE, StrategyRuleKeys.MAX_TENURE),
      total_weeks,
    );
  }

  private getUiFields(values: {
    index: number;
    key?: string;
    resolution_type: ResolutionType;
    format: StrategyFormat;
    payment_type: StrategyPaymentType;
    weekly_total_payment: number;
    total_weeks: number;
    interest_weeks: number;
    initial_total_payment: number;
    total_debt: number;
    clearance_weeks: number;
  }): Pick<StrategyResponse, StrategyResponseUiFields> {
    const hasWeeksReduced = values.clearance_weeks !== values.total_weeks;
    const main_payment_amount = values.initial_total_payment ? floorToClosest99(values.initial_total_payment) : 0;
    const sub_payment_amount = values.weekly_total_payment ? Math.round(values.weekly_total_payment) : 0;

    const sections = [
      { title: "Payable Today", amount: main_payment_amount },
      { title: "Payable Weekly", amount: sub_payment_amount },
    ];

    if (values.payment_type === StrategyPaymentType.MONTHLY) {
      sections[1] = {
        title: "Payable Monthly",
        amount: 4 * sub_payment_amount,
      };
    }

    const mainSection = sections[values.total_weeks === 0 ? 0 : 1];
    const subSection = sections[values.total_weeks === 0 ? 1 : 0];

    const hide: string[] = [];
    if (values.total_weeks === 0) {
      hide.push("sub_payment");
    }

    const debt_free_weeks = values.clearance_weeks || values.total_weeks;
    const debt_free_months = Math.floor(debt_free_weeks / 4);

    const fields = {
      key:
        values.key ||
        (values.format === StrategyFormat.DEFAULT ? `Fixed Plan ${values.index}` : `Custom Plan ${values.index}`),
      title: `Debt free in ${
        values.payment_type === StrategyPaymentType.MONTHLY ? debt_free_months : debt_free_weeks
      } ${values.payment_type === StrategyPaymentType.MONTHLY ? "month" : "week"}s${hasWeeksReduced ? "⁺" : ""}`,
      sub_title: `Pay <b>$${main_payment_amount}</b> today, remaining ${values.payment_type}.`,
      features: [
        "Interest Savings up to- <b>127%</b>",
        `Debt Clearance in ${values.total_weeks} Weeks`,
        "Best for Small Size Businesses",
      ],
      main_payment_title: mainSection.title,
      main_payment_amount: mainSection.amount,
      sub_payment_title: subSection.title,
      sub_payment_amount: subSection.amount,
      footer_text: "15% of our clients choose this plan",
      highlighted: false,
      hide,
      overline: {
        [StrategyFormat.DEFAULT]: "STANDARD",
        [StrategyFormat.CUSTOM]: "DYNAMIC",
        [StrategyFormat.MANAGERS_APPROVAL]: "CUSTOM V2",
      }[values.format],
    };

    if (values.payment_type === StrategyPaymentType.ONE_TIME) {
      fields.title = `Clear in ONE GO`;
      fields.sub_title = `Pay $<b>${fields.main_payment_amount}</b> as one-time payment, to clear your debt.`;
      fields.features = [
        `Save upto <b>$${floorToClosest99(values.total_debt - values.initial_total_payment)}</b> in interest!`,
        "Instant debt clearance",
        "Best for Medium Size Businesses",
      ];
      fields.footer_text = "10% of our clients choose this plan";
    } else if (values.interest_weeks === 0) {
      fields.footer_text = "<b>This is the best plan based on your revenue</b>";
      fields.highlighted = true;
    }

    return fields;
  }

  getGuardrails(): GuardRails {
    const MIN_MULTIPLIER = 0.4;
    const LAWSUIT_MULTIPLIER = 0.07;
    const MIN_BIAS = 150;
    const other_fees = this.context.service_weekly + this.weeklyLegalFee;

    const total_interest = this.getTotalInterestAmount();
    //? Should this be total_debt or total_creditor_payback_amount
    const total_creditor_payback = this.context.total_debt;
    // const total_creditor_payback = this.getTotalCreditorPaybackAmount();
    const total_payable = this.getTotalPayableAmount();

    const { interest_weeks } = this.getTenure();
    let min_custom_amount = Math.round(safeDivide(total_interest, interest_weeks) + MIN_BIAS + other_fees);

    if (this.context.lawsuits > 0) {
      const lawsuit_weekly = Math.round((total_creditor_payback * LAWSUIT_MULTIPLIER) / 4);

      min_custom_amount = Math.max(min_custom_amount, lawsuit_weekly);
    } else if (
      this.context.current_weekly_pay &&
      this.context.current_weekly_pay > 10 //? Questionable magic number
    ) {
      min_custom_amount = Math.max(min_custom_amount, Math.round(this.context.current_weekly_pay * MIN_MULTIPLIER));
    }

    const max_custom_weeks = Math.round(safeDivide(Math.ceil(total_payable), min_custom_amount));

    return { min_custom_amount, max_custom_weeks };
  }

  validateCustomParameters() {
    const guardrails = this.getGuardrails();

    if (this.context.weekly_budget && this.context.weekly_budget < guardrails.min_custom_amount) {
      throw new Error(`You can not go lower ${guardrails.min_custom_amount} weekly budget`);
    } else if (this.context.total_weeks && this.context.total_weeks > guardrails.max_custom_weeks) {
      throw new Error(`You can not go over ${guardrails.max_custom_weeks} weeks`);
    }

    return true;
  }

  private getWeeklyInterest({ updateProgramTenure }: { updateProgramTenure: (value: number) => boolean }) {
    const total_interest = this.getTotalInterestAmount();
    const initial_payment = this.getInitialPaymentAmount();
    const initial_extra_amount = this.getInitialExtraAmount();
    const initial_interest = Math.min(
      total_interest,
      initial_payment - (initial_extra_amount + this.config.creditor_split.initial_bias_amount),
    );

    const { interest_weeks } = this.getTenure();
    const total_weeks = this.getEstimatedTotalWeeks();

    const remaining_interest = total_interest - initial_interest;
    let weekly_interest = safeDivide(remaining_interest, interest_weeks);
    const total_payable = total_interest + this.getTotalCreditorPaybackAmount();

    const percentage = asPercentage(this.config.program_split?.meta?.percentage);

    if (this.config.program_split.format === ProgramSplitFormat.PERCENTAGE_OF_WEEKLY && percentage !== null) {
      const weekly_total = safeDivide(total_payable, total_weeks);
      const expected_weekly_interest = percentage * weekly_total;
      const program_weeks = Math.ceil(safeDivide(remaining_interest, expected_weekly_interest));

      const result = updateProgramTenure(program_weeks);
      if (result) weekly_interest = expected_weekly_interest;
    }

    return weekly_interest;
  }

  private getInitialPaymentFields(): Pick<
    StrategyResponse["common"],
    "is_initial_split" | "initial_amount" | "initial_split"
  > {
    const initial_split = this.config.program_split.initial_payment_timeline ?? 0;
    const is_initial_split = initial_split > 0;

    if (!is_initial_split) return { is_initial_split, initial_amount: undefined, initial_split: undefined };

    const initial_payment = this.getInitialPaymentAmount() - this.context.service_weekly;
    const initial_amount = safeDivide(initial_payment, initial_split) + this.context.service_weekly;
    return {
      is_initial_split,
      initial_amount,
      initial_split,
    };
  }

  buildStrategy(): StrategyResponse {
    const cycle = this.config.payment_type === StrategyPaymentType.MONTHLY ? "monthly" : "weekly";
    const total_interest = this.getTotalInterestAmount();
    const lawsuit_cost = this.getLawsuitCost();
    const initial_payment = this.getInitialPaymentAmount();
    const initial_extra_amount = this.getInitialExtraAmount();
    const initial_interest = Math.min(
      total_interest,
      initial_payment - (initial_extra_amount + this.config.creditor_split.initial_bias_amount),
    );

    const total_payback = this.getTotalPayableAmount();
    let { total_weeks, interest_weeks } = this.getTenure();
    const weekly_extra = this.context.service_weekly + lawsuit_cost.weekly;
    const total_weekly_extra = weekly_extra * (total_weeks - (cycle === "monthly" ? 3 : 1));
    const total_payback_with_weekly_extra = total_payback + total_weekly_extra;

    const remaining_total = total_payback_with_weekly_extra - initial_payment;

    const weekly_total = safeDivide(remaining_total, total_weeks);
    const weekly_interest = this.getWeeklyInterest({
      updateProgramTenure: (program_tenure) => {
        const isValid = this.validateValue(
          program_tenure,
          StrategyRuleKeys.MIN_PROGRAM_TENURE,
          StrategyRuleKeys.MAX_PROGRAM_TENURE,
        );

        if (isValid === 0) {
          interest_weeks = program_tenure;
        }

        return isValid === 0;
      },
    });

    const split_rows = {
      creditor_start: 0,
      creditor_end: total_weeks,
      /** @deprecated */
      creditor_max_weekly: [] as number[],
      program_start: 0,
      program_end: interest_weeks,
      program_weekly: weekly_interest,
      initial_bias_amount: this.config.initial_bias_amount,
    };

    if (initial_interest === 0) {
      split_rows.program_start = cycle === "monthly" ? 4 : 1;
    }
    if (cycle === "monthly") {
      split_rows.program_end = Math.ceil(interest_weeks / 4) * 4 - 1;
    }
    if (weekly_interest + weekly_extra >= weekly_total) {
      if (weekly_interest * interest_weeks + initial_interest > total_interest) {
        split_rows.creditor_start = interest_weeks - 1;
      } else {
        split_rows.creditor_start = interest_weeks;
      }
    }

    const clearance_weeks = this.calculateDisplayTimeline();
    const uiFields = this.getUiFields({
      index: this.config.index,
      key: this.config.key,
      resolution_type: this.config.resolution_type,
      format: this.config.format,
      payment_type: this.config.payment_type,
      weekly_total_payment: weekly_total,
      total_weeks,
      interest_weeks,
      initial_total_payment: initial_payment,
      total_debt: this.context.total_debt,
      clearance_weeks,
    });
    const initialPaymentFields = this.getInitialPaymentFields();
    const is_custom_divided = total_weeks !== interest_weeks && interest_weeks > 0;

    if (total_weeks) {
      const creditor_payment_on_first_week = this.config.creditor_split.initial_bias_amount;
      const creditor_payment_on_interest_weeks = weekly_total - (weekly_interest + this.context.service_weekly);

      split_rows.creditor_max_weekly.push(creditor_payment_on_first_week, creditor_payment_on_interest_weeks);
    } else {
      const creditor_payment_at_once =
        initial_payment - (initial_interest + this.context.service_weekly + lawsuit_cost.initial);

      split_rows.creditor_max_weekly.push(creditor_payment_at_once);
    }

    if (is_custom_divided) {
      split_rows.creditor_max_weekly.push(weekly_total - this.context.service_weekly);
    }

    return {
      slug: this.config.slug,
      ...uiFields,
      total_weeks,
      selector_key: this.config.slug,
      program_tenure: interest_weeks,
      ledger_type: DistributionType.FIXED,
      unavailable: isTruthful(this.config.meta?.disabled),
      resolution_type: this.config.resolution_type,
      format: this.config.format,
      version: 2,
      payment_type: this.config.payment_type,
      interest_calculation_strategy: this.config.interest_calculation_strategy,
      target_weekly_calculation_strategy: this.config.target_weekly_calculation_strategy,
      program_split_format: this.config.program_split.format,
      creditor_payment_format: this.config.creditor_split.pay_format,
      display_timeline: clearance_weeks,
      common: {
        lawsuit_initial: this.initialLegalFee,
        lawsuit_weekly: this.weeklyLegalFee,
        service_fee_initial: this.initialServiceFee,
        service_fee_weekly: this.weeklyServiceFee,
        interest: total_interest,
        interest_rate: this.config.program_fee + this.config.success_fee,
        initial_payment: initial_payment,
        initial_program_fee: initial_interest,
        weekly_payments: weekly_total,
        total_debt: this.context.total_debt,
        creditor_payable: this.getTotalCreditorPaybackAmount(),
        total_payable: total_payback_with_weekly_extra,
        strategy_clause: this.config.index,
        is_custom_divided,
        is_managers_approval:
          this.config.format === StrategyFormat.MANAGERS_APPROVAL &&
          [ManagerApprovalStatus.APPROVED, ManagerApprovalStatus.SUGGESTED].includes(
            this.context.manager_approval_status,
          ),
        selector_idx: this.config.index,
        split_rows,
        ...initialPaymentFields,
        split_at: clearance_weeks ? 1 + clearance_weeks : -1,
      },
    };
  }
}

export class LegacyStrategyGenerator {
  private readonly context: StrategyGenerationClientContext;

  constructor(context: StrategyGenerationClientContext) {
    this.context = context;
  }

  private getLawsuitCost() {
    return getLawsuitCost(this.context);
  }

  buildFromCustomConfigV1(config: CustomStrategy & { index: number }): StrategyResponse {
    const { initial: lawsuit_initial, weekly: lawsuit_weekly } = this.getLawsuitCost();

    const _total_interest = Math.ceil(this.context.total_debt * config.net_commission) / 100;

    const strat_three_weekly_program_fee =
      Math.round(
        (this.context.total_debt * (config.net_commission - config.initial_pay)) / config.commission_timeline,
      ) / 100; // by 100 outside to adjust the fraction
    const strat_three_total_pay = (config.bias_amount ?? 0) + this.context.total_debt * config.multiplier;
    const strat_three_first_payment =
      Math.round((config.initial_payment * 100) / config.initial_payment_timeline) / 100;

    let creditor_week_entries: number[] = [0];

    if (config.creditor_estimate_split.length) {
      config.creditor_estimate_split.forEach((_c_estimate: { amount: number; weeks: number }) => {
        creditor_week_entries.push(_c_estimate.amount);
      });
    } else {
      creditor_week_entries = [0, -1, -1, config.creditor_estimate];
    }

    return {
      slug: slugify(`Custom Plan ${config.index}`),
      key: `Custom Plan ${config.index}`,
      title: `Debt free in ${config.display_timeline ?? config.commission_timeline} Weeks⁺`,
      sub_title: `Pay $<b>${strat_three_first_payment} today</b>, remaining weekly.`,
      features: [
        "Interest Savings up to- <b>127%</b>",
        `Debt Clearance in ${config.weeks} Weeks`,
        "Best for Small Size Businesses",
      ],
      unavailable: false,
      hide: [],
      selector_key: slugify(`Custom Plan ${config.index}`),
      display_timeline: (config.weeks ?? 0) * 0.8,
      ledger_type: DistributionType.FIXED,
      main_payment_title: "Payable Weekly",
      main_payment_amount: config.weekly_budget * (1 + config.card_mdr),
      sub_payment_title: "Payable Today",
      sub_payment_amount: strat_three_first_payment,
      footer_text: config.include_commission_payment
        ? `Best plan under ${config.weeks} weeks`
        : `Payment reduces after ${config.commission_timeline} weeks`,
      overline: "CUSTOM V1",
      highlighted: config.index === 1,
      total_weeks: config.weeks,
      program_tenure: config.commission_timeline,
      resolution_type: ResolutionType.NEGOTIATE,
      format: StrategyFormat.MANAGERS_APPROVAL,
      version: 1,
      payment_type: StrategyPaymentType.WEEKLY,
      interest_calculation_strategy: InterestCalculationStrategy.PERCENTAGE,
      target_weekly_calculation_strategy: TargetWeeklyCalculationStrategy.PRIOR_BIAS,
      creditor_payment_format: CreditorPayFormat.FCDS,
      program_split_format: ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE,
      common: {
        lawsuit_initial,
        lawsuit_weekly,
        service_fee_weekly: config.service_weekly ?? this.context.service_weekly,
        interest: _total_interest,
        interest_rate:
          config.net_commission < 1 ? config.net_commission : Number((config.net_commission / 100).toFixed(2)),
        initial_payment: strat_three_first_payment,
        weekly_payments: config.weekly_budget * (1 + config.card_mdr),
        total_payable:
          (strat_three_total_pay + lawsuit_weekly + (config.service_weekly ?? this.context.service_weekly)) *
          (1 + config.card_mdr),
        total_debt: this.context.total_debt,
        creditor_payable: 0,
        initial_program_fee: 0,
        strategy_clause: config.index,
        is_custom_divided: true,
        is_managers_approval: true,
        selector_idx: config.index,
        split_rows: {
          program_start: 0,
          program_end: config.commission_timeline,
          program_weekly: strat_three_weekly_program_fee,
          creditor_start: 0,
          creditor_end: config.commission_timeline,
          creditor_max_weekly: creditor_week_entries,
          initial_bias_amount: config.bias_amount ?? 0,
        },
        is_initial_split: config.initial_payment_timeline !== 1,
        initial_amount: config.initial_payment
          ? Math.round(config.initial_payment / (config.initial_payment_timeline ?? 1))
          : strat_three_first_payment,
        initial_split: config.initial_payment_timeline ?? 1,
        split_at: config.display_timeline
          ? 1 + config.display_timeline
          : config.commission_timeline
            ? 1 + config.commission_timeline
            : -1,
      },
    };
  }
}

export const generateStrategySlug = (config: Omit<StrategyConfigV2, "slug">) => {
  const components: Record<
    ResolutionType | StrategyFormat | StrategyPaymentType | TargetWeeklyCalculationStrategy,
    string
  > = {
    [ResolutionType.NEGOTIATE]: "n",
    [ResolutionType.SETTLEMENT]: "s",

    [StrategyFormat.DEFAULT]: "d",
    [StrategyFormat.CUSTOM]: "c",
    [StrategyFormat.MANAGERS_APPROVAL]: "m",

    [StrategyPaymentType.ONE_TIME]: "o",
    [StrategyPaymentType.WEEKLY]: "w",
    [StrategyPaymentType.MONTHLY]: "m",

    [TargetWeeklyCalculationStrategy.FIXED]: "f",
    [TargetWeeklyCalculationStrategy.LOW_WEEKLY]: "l",
    [TargetWeeklyCalculationStrategy.PRIOR_BIAS]: "p",
    [TargetWeeklyCalculationStrategy.COMPACT]: "c",
  };

  const configCode = `${components[config.resolution_type]}0${
    components[config.format]
  }1${components[config.payment_type]}2${components[config.target_weekly_calculation_strategy]}3`;

  return slugify(configCode + "-" + randomBytes(4).toString().slice(0, 4), { lower: true });
};

export const preparePaymentTable = (
  strategy: StrategyResponse,
  options: { accumulateDecimals?: boolean } = {},
): PaymentTable => {
  const { accumulateDecimals = true } = options;

  const adjustPaymentRow = (row: PaymentTableRow) => {
    if (row.program_fee < 0) {
      row.payment += Math.abs(row.program_fee);
      row.program_fee = 0;
    }
    if (row.creditor < 0) {
      row.payment += Math.abs(row.creditor);
      row.creditor = 0;
    }

    return row;
  };

  const stripDecimals = (row: PaymentTableRow) => {
    const programFloor = Math.floor(row.program_fee);
    const creditorFloor = Math.floor(row.creditor);
    const programExtracted = row.program_fee - programFloor;
    const creditorExtracted = row.creditor - creditorFloor;

    row.program_fee = programFloor;
    row.creditor = creditorFloor;
    row.payment = row.program_fee + row.service_fee + row.legal_fee + row.creditor;

    return { program: programExtracted, creditor: creditorExtracted };
  };

  let first_row: PaymentTable["first_row"] = adjustPaymentRow({
    week: 1,
    payment: strategy.common.initial_payment,
    program_fee: strategy.common.initial_program_fee,
    service_fee: strategy.common.service_fee_weekly,
    legal_fee: strategy.common.lawsuit_initial,
    creditor:
      strategy.common.initial_payment -
      (strategy.common.initial_program_fee + strategy.common.service_fee_weekly + strategy.common.lawsuit_initial),
  });

  if (strategy.common.is_initial_split && strategy.common.initial_split) {
    first_row.payment =
      strategy.common.initial_amount ?? safeDivide(strategy.common.initial_payment, strategy.common.initial_split);

    first_row.program_fee = safeDivide(first_row.program_fee, strategy.common.initial_split);

    first_row.legal_fee = safeDivide(first_row.legal_fee, strategy.common.initial_split);

    first_row.creditor = first_row.payment - (first_row.program_fee + first_row.service_fee + first_row.legal_fee);

    first_row = adjustPaymentRow(first_row);
  }

  const totals: PaymentTable["totals"] = {
    week: strategy.total_weeks,
    payment: strategy.common.total_payable,
    program_fee: strategy.common.interest,
    service_fee:
      (strategy.common.service_fee_initial ?? strategy.common.service_fee_weekly) +
      strategy.total_weeks * strategy.common.service_fee_weekly,
    legal_fee: strategy.common.lawsuit_initial + strategy.total_weeks * strategy.common.lawsuit_weekly,
    creditor: 0,
  };

  totals.creditor = totals.payment - (totals.program_fee + totals.service_fee + totals.legal_fee);

  const repeat_rows: PaymentTable["repeat_rows"] = [];
  if (strategy.total_weeks > 0) {
    if (strategy.common.is_custom_divided) {
      const weekly_creditor =
        strategy.common.weekly_payments - (strategy.common.service_fee_weekly + strategy.common.lawsuit_weekly);

      const program_end = strategy.common.split_rows.program_end;
      const creditor_end = strategy.common.split_rows.creditor_end;

      const interest_row = adjustPaymentRow({
        week: 1,
        payment: strategy.common.weekly_payments,
        program_fee: strategy.common.split_rows.program_weekly,
        service_fee: strategy.common.service_fee_weekly,
        legal_fee: strategy.common.lawsuit_weekly,
        creditor: weekly_creditor - strategy.common.split_rows.program_weekly,
      });

      const interest_offset =
        program_end * interest_row.program_fee - (strategy.common.interest - strategy.common.initial_program_fee);

      const add_adjustment_row = Math.floor(interest_offset) > 0;

      const interest_row_count = add_adjustment_row ? program_end - 1 : program_end;

      if (interest_row_count > 0) {
        repeat_rows.push({
          row: interest_row,
          count: interest_row_count,
        });
      }

      if (add_adjustment_row) {
        const program_fee = strategy.common.split_rows.program_weekly - interest_offset;
        const creditor =
          strategy.common.weekly_payments -
          (strategy.common.lawsuit_weekly + strategy.common.service_fee_weekly + program_fee);

        const adjustment_row = adjustPaymentRow({
          week: program_end,
          payment: strategy.common.weekly_payments,
          program_fee,
          service_fee: strategy.common.service_fee_weekly,
          legal_fee: strategy.common.lawsuit_weekly,
          creditor,
        });

        repeat_rows.push({
          row: adjustment_row,
          count: 1,
        });
      }

      const creditor_row = adjustPaymentRow({
        week: program_end + 1,
        payment: strategy.common.weekly_payments,
        program_fee: 0,
        service_fee: strategy.common.service_fee_weekly,
        legal_fee: strategy.common.lawsuit_weekly,
        creditor: weekly_creditor,
      });

      const creditor_weeks = creditor_end - program_end;
      if (creditor_weeks > 0) {
        repeat_rows.push({
          row: creditor_row,
          count: creditor_weeks,
        });
      }

      let creditor_offset = -(strategy.common.creditor_payable ?? strategy.common.total_debt);
      for (const rr of repeat_rows) {
        creditor_offset += rr.count * rr.row.creditor;
      }

      const add_creditor_adjustment_row = Math.floor(creditor_offset) > 0;
      if (add_creditor_adjustment_row && creditor_weeks > 1) {
        const last_row = repeat_rows.pop()!;

        const adjusted_creditor = last_row.row.creditor - creditor_offset;
        const adjusted_payment =
          adjusted_creditor + last_row.row.program_fee + last_row.row.service_fee + last_row.row.legal_fee;

        const creditor_adjustment_row = adjustPaymentRow({
          week: creditor_end,
          payment: adjusted_payment,
          program_fee: last_row.row.program_fee,
          service_fee: last_row.row.service_fee,
          legal_fee: last_row.row.legal_fee,
          creditor: adjusted_creditor,
        });

        repeat_rows.push(
          {
            row: last_row.row,
            count: last_row.count - 1,
          },
          {
            row: creditor_adjustment_row,
            count: 1,
          },
        );
      }
    } else {
      const weekly_interest = round(
        safeDivide(strategy.common.interest - strategy.common.initial_program_fee, strategy.total_weeks),
        2,
      );
      const weekly_creditor = round(
        strategy.common.weekly_payments -
          (weekly_interest + strategy.common.service_fee_weekly + strategy.common.lawsuit_weekly),
        2,
      );

      const row = adjustPaymentRow({
        week: 1,
        payment: strategy.common.weekly_payments,
        program_fee: weekly_interest,
        service_fee: strategy.common.service_fee_weekly,
        legal_fee: strategy.common.lawsuit_weekly,
        creditor: weekly_creditor,
      });

      repeat_rows.push({
        row,
        count: strategy.total_weeks,
      });
    }
  }

  if (accumulateDecimals && repeat_rows.length > 0) {
    let accumulatedProgram = 0;
    let accumulatedCreditor = 0;

    for (const rr of repeat_rows) {
      const extracted = stripDecimals(rr.row);
      accumulatedProgram += extracted.program * rr.count;
      accumulatedCreditor += extracted.creditor * rr.count;
    }

    const firstProgramExtracted = first_row.program_fee - Math.floor(first_row.program_fee);
    const firstCreditorExtracted = first_row.creditor - Math.floor(first_row.creditor);
    first_row.program_fee = Math.floor(first_row.program_fee);
    first_row.creditor = Math.floor(first_row.creditor);
    first_row.payment = first_row.program_fee + first_row.service_fee + first_row.legal_fee + first_row.creditor;
    accumulatedProgram += firstProgramExtracted;
    accumulatedCreditor += firstCreditorExtracted;

    const splitOffLastWeek = (idx: number): PaymentTableRow => {
      const target = repeat_rows[idx];
      if (target.count === 1) return target.row;

      const splitRow: PaymentTableRow = {
        week: target.row.week + target.count - 1,
        payment: target.row.payment,
        program_fee: target.row.program_fee,
        service_fee: target.row.service_fee,
        legal_fee: target.row.legal_fee,
        creditor: target.row.creditor,
      };

      repeat_rows.splice(idx, 1, { row: target.row, count: target.count - 1 }, { row: splitRow, count: 1 });

      return splitRow;
    };

    if (accumulatedProgram > 0) {
      let programIdx = -1;
      for (let i = repeat_rows.length - 1; i >= 0; i--) {
        if (repeat_rows[i].row.program_fee > 0) {
          programIdx = i;
          break;
        }
      }

      if (programIdx !== -1) {
        const targetRow = splitOffLastWeek(programIdx);

        if (targetRow.creditor > 0) {
          const absorbed = Math.min(targetRow.creditor, accumulatedProgram);
          const overflow = accumulatedProgram - absorbed;

          targetRow.program_fee += accumulatedProgram;
          targetRow.creditor -= absorbed;
          targetRow.payment += overflow;

          accumulatedCreditor += absorbed;
        } else {
          targetRow.program_fee += accumulatedProgram;
          targetRow.payment += accumulatedProgram;
        }
      } else {
        accumulatedCreditor += accumulatedProgram;
      }
    }

    if (accumulatedCreditor > 0) {
      let creditorIdx = -1;
      for (let i = repeat_rows.length - 1; i >= 0; i--) {
        if (repeat_rows[i].row.creditor > 0) {
          creditorIdx = i;
          break;
        }
      }

      if (creditorIdx !== -1) {
        const targetRow = splitOffLastWeek(creditorIdx);
        targetRow.creditor += accumulatedCreditor;
        targetRow.payment += accumulatedCreditor;
      }
    }
  }

  return {
    headers: Object.keys(first_row),
    resolution: strategy.payment_type === StrategyPaymentType.MONTHLY ? "month" : "week",
    first_row,
    repeat_rows,
    totals,
    weeks: strategy.total_weeks,
    lawsuit: strategy.common.lawsuit_initial > 0,
  };
};

const reverseCalculateRepeatRows = (ledgerEntries: LedgerDto[]): PaymentTable["repeat_rows"] => {
  const filtered = ledgerEntries.filter((entry) => entry.category !== LEDGER_CATEGORY.SKIP);

  const total = filtered.length - 1;
  const repeatRows: PaymentTable["repeat_rows"] = [];

  const hasChanged = (a: number, b: number) => Math.abs(a - b) >= 1;
  const isDifferentRow = (a: LedgerDto, b: LedgerDto) => {
    const totalChanged = hasChanged(a.amount, b.amount);
    const interestChanged = hasChanged(a.distribution.program_fee, b.distribution.program_fee);
    const creditorChanged = hasChanged(a.distribution.creditor_total, b.distribution.creditor_total);
    const legalChanged = hasChanged(a.distribution.legal_fee, b.distribution.legal_fee);

    return totalChanged || interestChanged || creditorChanged || legalChanged;
  };

  const ledgerEntryToRepeatRow = (ledger: LedgerDto): PaymentTable["repeat_rows"][number]["row"] => ({
    week: ledger.week,
    payment: ledger.amount,
    program_fee: ledger.distribution.program_fee,
    creditor: ledger.distribution.creditor_total,
    service_fee: ledger.distribution.service_fee,
    legal_fee: ledger.distribution.legal_fee,
  });

  let runningRow: PaymentTableRow | undefined;
  for (let index = 1; index <= total; index++) {
    const hasChanged = isDifferentRow(filtered[index - 1], filtered[index]);
    const addToRepeatRows = () => {
      if (!runningRow) return;
      const currentIndex =
        ledgerEntries[runningRow.week].category === LEDGER_CATEGORY.MONTHLY ? runningRow.week / 4 : runningRow.week;

      repeatRows.push({
        row: runningRow,
        count: index - currentIndex,
      });
    };

    if (hasChanged) {
      if (runningRow) addToRepeatRows();
      runningRow = ledgerEntryToRepeatRow(filtered[index]);
    }
  }

  if (runningRow) {
    repeatRows.push({
      row: runningRow,
      count: Math.floor(ledgerEntries.length / 4) * 4 - runningRow.week,
    });
  }

  return repeatRows;
};

const calculateTotalRow = (paymentTable: PaymentTable) => {
  const totalRow: PaymentTable["totals"] = {
    week: 1,
    payment: paymentTable.first_row.payment,
    program_fee: paymentTable.first_row.program_fee,
    service_fee: paymentTable.first_row.service_fee,
    legal_fee: paymentTable.first_row.legal_fee,
    creditor: paymentTable.first_row.creditor,
  };

  for (const { row, count } of paymentTable.repeat_rows) {
    totalRow.payment += row.payment * count;
    totalRow.program_fee += row.program_fee * count;
    totalRow.service_fee += row.service_fee * count;
    totalRow.legal_fee += row.legal_fee * count;
    totalRow.creditor += row.creditor * count;
    totalRow.week += count;
  }

  return totalRow;
};

export const adjustPaymentTable = (paymentTable: PaymentTable, ledgerEntries: LedgerDto[]): PaymentTable => {
  const adjusted = omitAndMakeOptional(paymentTable, "repeat_rows", "totals");

  adjusted.repeat_rows = reverseCalculateRepeatRows(ledgerEntries);
  adjusted.totals = calculateTotalRow(adjusted as PaymentTable);

  return adjusted as PaymentTable;
};

const convertToMonthlyLedger = (weeklyLedgerEntries: LedgerDto[]) => {
  const monthlyLedgerEntries: LedgerDto[] = weeklyLedgerEntries;

  let carryProgramFee = 0;
  let carryCreditorFee = 0;
  for (const ledgerEntry of weeklyLedgerEntries) {
    const { week, amount, distribution } = ledgerEntry;
    const { program_fee, legal_fee, service_fee, creditor_total } = distribution;

    // It's a non paying week
    if (week % 4) {
      const month = week - (week % 4);

      if (month > 0) {
        monthlyLedgerEntries[month].amount += amount;
        monthlyLedgerEntries[month].distribution.program_fee += program_fee;
        monthlyLedgerEntries[month].distribution.legal_fee += legal_fee;
        monthlyLedgerEntries[month].distribution.service_fee += service_fee;
        monthlyLedgerEntries[month].distribution.creditor_total += creditor_total;
      } else {
        carryProgramFee += program_fee;
        carryCreditorFee += creditor_total;
      }

      if (!monthlyLedgerEntries[week]) continue;
      monthlyLedgerEntries[week].category = LEDGER_CATEGORY.SKIP;
      monthlyLedgerEntries[week].amount = 0;
      monthlyLedgerEntries[week].distribution.program_fee = 0;
      monthlyLedgerEntries[week].distribution.legal_fee = 0;
      monthlyLedgerEntries[week].distribution.service_fee = 0;
      monthlyLedgerEntries[week].distribution.creditor_total = 0;
    } else if (monthlyLedgerEntries[week]) {
      monthlyLedgerEntries[week].category = week === 0 ? LEDGER_CATEGORY.INITIAL : LEDGER_CATEGORY.MONTHLY;
    }
  }

  const reversed = [...monthlyLedgerEntries].reverse();
  if (carryProgramFee > 0) {
    const lastProgramWeek = reversed.find(
      (entry) => entry.category !== LEDGER_CATEGORY.SKIP && entry.distribution.program_fee > 0,
    );
    if (lastProgramWeek) {
      const updatedCreditorValue = 0;
      const updatedProgramValue = lastProgramWeek.distribution.program_fee + carryProgramFee;
      const updatedTotalValue =
        lastProgramWeek.amount + (carryProgramFee - lastProgramWeek.distribution.creditor_total);

      carryCreditorFee += lastProgramWeek.distribution.creditor_total;
      monthlyLedgerEntries[lastProgramWeek.week].amount = updatedTotalValue;
      monthlyLedgerEntries[lastProgramWeek.week].distribution.program_fee = updatedProgramValue;
      monthlyLedgerEntries[lastProgramWeek.week].distribution.creditor_total = updatedCreditorValue;
    }
  }

  return monthlyLedgerEntries;
};

/** Application Id and affiliate data is incorrect */
export const createLedgerEntries = ({
  payment_table,
  strategy,
}: {
  payment_table: PaymentTable;
  strategy: StrategyResponse;
  preview?: boolean;
}) => {
  const order_date = new Date();

  let ledgerEntries: CreateLedgerDto[] = [];

  let remaining_total_fee = round(payment_table.totals.payment);
  let remaining_program_fee = round(payment_table.totals.program_fee);
  let remaining_creditor_fee = round(payment_table.totals.creditor);

  if (remaining_program_fee < 0) {
    remaining_total_fee += Math.abs(remaining_program_fee);
    remaining_program_fee = 0;
  }

  if (remaining_creditor_fee < 0) {
    remaining_total_fee += Math.abs(remaining_creditor_fee);
    remaining_creditor_fee = 0;
  }

  const tableEntries = Array.from({
    length: payment_table.weeks + 1,
  }).map((_, idx) => {
    const entry: PaymentEntryTable = {
      week: idx.toString(),
      payment: 0,
      program_fee: 0,
      service_fee: 0,
      creditor: 0,
    };

    const isInitialZeroWeek = strategy.payment_type === StrategyPaymentType.MONTHLY && idx < 4;

    if (idx === 0) {
      const payment = Math.min(round(payment_table.first_row.payment), remaining_total_fee);
      const program_fee = Math.min(round(payment_table.first_row.program_fee), remaining_program_fee);
      const creditor = Math.min(round(payment_table.first_row.creditor), remaining_creditor_fee);

      entry.payment = payment_table.first_row.payment;
      entry.program_fee = payment_table.first_row.program_fee;
      entry.service_fee = payment_table.first_row.service_fee;
      entry.creditor = payment_table.first_row.creditor;
      entry.legal_fee = payment_table.first_row.legal_fee;

      remaining_total_fee = round(remaining_total_fee - payment);
      remaining_program_fee = round(remaining_program_fee - program_fee);
      remaining_creditor_fee = round(remaining_creditor_fee - creditor);
    } else {
      for (const rr of payment_table.repeat_rows.sort((a, b) => a.row.week - b.row.week)) {
        const start = rr.row.week;
        const end = start + rr.count;

        if (idx >= start && idx < end) {
          let payment = Math.min(round(rr.row.payment), remaining_total_fee);
          let program_fee = Math.min(round(rr.row.program_fee), remaining_program_fee);
          let creditor = payment - (program_fee + rr.row.service_fee + rr.row.legal_fee);
          let legal_fee = rr.row.legal_fee;

          // When initial split is true (Initial Payment is breaking into multiple payment cycles)
          if (strategy.common.is_initial_split && strategy.common.initial_split) {
            const isMonthly =
              payment_table.resolution === "month" && idx % 4 === 0 && idx / 4 <= strategy.common.initial_split;
            const isWeekly = payment_table.resolution !== "month" && idx <= strategy.common.initial_split;

            if (isMonthly || isWeekly) {
              payment += payment_table.first_row.payment;
              program_fee += payment_table.first_row.program_fee;
              legal_fee += payment_table.first_row.legal_fee;
              creditor += payment_table.first_row.creditor;
            }
          }

          entry.payment = payment - (isInitialZeroWeek ? rr.row.service_fee + legal_fee : 0);
          entry.program_fee = program_fee;
          entry.service_fee = isInitialZeroWeek ? 0 : rr.row.service_fee;
          entry.legal_fee = isInitialZeroWeek ? 0 : legal_fee;
          entry.creditor = creditor;

          remaining_total_fee = round(remaining_total_fee - payment);
          remaining_program_fee = round(remaining_program_fee - program_fee);
          remaining_creditor_fee = round(remaining_creditor_fee - creditor);
        }
      }
    }

    return entry;
  });

  remaining_total_fee = round(payment_table.totals.payment);
  remaining_program_fee = round(payment_table.totals.program_fee);
  remaining_creditor_fee = round(payment_table.totals.creditor);
  const remaining_service_fee = round(payment_table.totals.service_fee);
  const remaining_legal_fee = round(payment_table.totals.legal_fee);

  tableEntries.forEach((entry) => {
    if (!entry.payment) return;
    const week = Number.parseInt(entry.week);

    ledgerEntries.push({
      application_id: "",
      transaction_type: TRANSACTION_TYPE.CREDIT,
      category: week === 0 ? LEDGER_CATEGORY.INITIAL : LEDGER_CATEGORY.WEEKLY,
      week,
      amount: entry.payment,
      paid: 0,
      status: TRANSACTION_STATUS.PROPOSED,
      distribution_type: DistributionType.FIXED,
      distribution: {
        program_fee: entry.program_fee ?? 0,
        service_fee: entry.service_fee ?? 0,
        legal_fee: entry.legal_fee ?? 0,
        creditor_total: entry.creditor ?? 0,
      },
      remaining_amount: {
        total: round(remaining_total_fee - entry.payment),
        program_fee: round(remaining_program_fee - entry.program_fee),
        service_fee: round(remaining_service_fee - entry.service_fee),
        legal_fee: round(remaining_legal_fee - (entry.legal_fee ?? 0)),
        creditor_fee: round(remaining_creditor_fee - entry.creditor),
      },
      creditors: [],
      affiliates: [],
      order_date: new Date(order_date),
      lock: LOCK_STATUS.UNLOCKED,
    });
  });

  if (strategy.payment_type === StrategyPaymentType.MONTHLY) ledgerEntries = convertToMonthlyLedger(ledgerEntries);
  return ledgerEntries;
};

export const getStrategyContext = (
  application: ApplicationDto,
  custom: Pick<StrategyGenerationClientContext, "weekly_budget" | "total_weeks">,
): StrategyGenerationClientContext => {
  const weekly_revenue = application.summary?.monthly_revenue ? application.summary?.monthly_revenue / 4 : undefined;

  let weekly_profit = application.summary?.monthly_profit ? application.summary.monthly_profit / 4 : undefined;

  if (weekly_profit === undefined && weekly_revenue && application.summary?.profit_percentage) {
    weekly_profit = (weekly_revenue * application.summary.profit_percentage) / 100;
  }

  return {
    appKey: application.app_key,
    total_weeks: custom.total_weeks,
    total_debt: application.enrolled_debt || application.summary?.current_debt || 0,
    lawsuits: application.total_lawsuits || 0,
    loans: application.total_loans || application.loans?.length || 0,
    weekly_budget: custom.weekly_budget,
    no_lawsuit_pay: {
      // one_time: application.summary?.lawsuit_insurance ? 850 : 0,
      // monthly: application.summary?.lawsuit_insurance ? 145 : 0,
      one_time: 850,
      monthly: 145,
    },
    with_lawsuit_pay: {
      one_time: 995,
      monthly: 145,
    },
    service_weekly: 25,
    current_weekly_pay: application.summary?.weekly_payments,
    monthly_revenue: application.summary?.monthly_revenue,
    monthly_profit: application.summary?.monthly_profit,
    profit_percentage: application.summary?.profit_percentage,
    weekly_profit,
    manager_approval_status: application.manager_approval_status ?? ManagerApprovalStatus.NONE,
  };
};

type SelectorKeyValues = {
  identifier: string;
  orgCode: string | null;
  total_weeks: number | null;
  weekly_budget: number | null;
};

export class SelectorKey {
  private readonly raw: string | null = null;
  private readonly orgCode: string | null = null;
  private readonly identifier: string | null = null;
  private readonly total_weeks: number | null = null;
  private readonly weekly_budget: number | null = null;

  decode(): SelectorKeyValues | null {
    if (!this.raw) return null;

    const components = this.raw
      .split(":")
      .map((c) => c.trim())
      .filter(Boolean);
    const identifier = components.find((c) => c.startsWith("id_"))?.slice(3);
    if (!identifier) return null;

    const total_weeks = components.find((c) => c.startsWith("tw_"))?.slice(3) ?? null;
    const weekly_budget = components.find((c) => c.startsWith("wb_"))?.slice(3) ?? null;
    const orgCode = components.find((c) => c.startsWith("oc_"))?.slice(3) ?? null;

    return {
      identifier,
      total_weeks: z.coerce.number().safeParse(total_weeks).data ?? null,
      weekly_budget: z.coerce.number().safeParse(weekly_budget).data ?? null,
      orgCode,
    };
  }

  compareId(value: undefined | null | string | SelectorKeyValues | SelectorKey) {
    if (!value) return false;
    else if (typeof value === "string") return new SelectorKey(value).identifier === this.identifier;
    else if (value instanceof SelectorKey) return value.identifier === this.identifier;
    else if (typeof value === "object" && "identifier" in value) return value.identifier === this.identifier;
    return false;
  }

  encode() {
    if (!this.identifier) return null;
    const components: string[] = [`id_${this.identifier}`];
    if (this.orgCode !== null) components.push(`oc_${this.orgCode}`);
    if (this.total_weeks !== null) components.push(`tw_${this.total_weeks}`);
    if (this.weekly_budget !== null) components.push(`wb_${this.weekly_budget}`);

    return components.join(":");
  }

  constructor(value: string | SelectorKeyValues) {
    if (typeof value === "string") {
      this.raw = value;
      const decoded = this.decode();
      if (decoded) {
        this.identifier = decoded.identifier;
        this.total_weeks = decoded.total_weeks;
        this.weekly_budget = decoded.weekly_budget;
        this.orgCode = decoded.orgCode;
      } else {
        this.identifier = value;
      }
    } else {
      this.identifier = value.identifier;
      this.total_weeks = value.total_weeks;
      this.weekly_budget = value.weekly_budget;
      this.orgCode = value.orgCode;
      this.raw = this.encode();
    }
  }
}
